import { Dispatch, SetStateAction, useCallback, useRef } from 'react';
import { useRecoilValue } from 'recoil';
import { Toggle } from '@/ui/toggle';
import settingsState from '@/stores/settingsStore';
import { useLocalization } from '@/services/localizationService';
import { useTracker } from '@/core/tracking';
import infoIcon from '@/public/images/icons/question-mark.svg';
import { EditorSection, ImageEditorElements } from '@/services/editorService';
import { editorLoadingState } from '@/stores/editorStore';
import { downloadMediaState } from '@/stores/mediaStore';
import greenCheckIcon from '@/public/images/icons/check--green.svg';
import gear from '@/public/images/icons/gear.svg';
import upArrowIcon from '@/public/images/icons/up-small--format.svg';
import downArrowIcon from '@/public/images/icons/down-small--format.svg';
import consent from '@/public/images/icons/consent.svg';
import Spinner from '@/ui/content/Spinner';
import useOnClickOutside from '@/core/listeners/useOnClickOutside';
import { TutorialProps } from '@/services/tutorialService/tutorialTypes';
import { tutorialState } from '@/stores/tutorialStore';
import { isLoggedInSelector } from '@/stores/authStore';
import { useAiToolbar } from '@/features/imageEditor/aiToolbar/useAiToolbar';
import { SectionName } from '@/services/settingsService/aiTypes';
import Dropdown from '@/features/imageEditor/aiToolbar/dropdown/Dropdown';
import EditorPreviews from '@/features/imageEditor/aiToolbar/editorPreviews/EditorPreviews';
import equal from 'fast-deep-equal';
import { EditorFilters } from './editorFilters';

export function AiToolbar({
  isAiSettingsDropdownOpen,
  newModelsTutorialId,
  onCtaActiveChange,
  saveSettingsTutorialId,
  setIsAiSettingsDropdownOpen,
}: {
  isAiSettingsDropdownOpen: boolean;
  newModelsTutorialId: string;
  onCtaActiveChange: (event: boolean) => void;
  saveSettingsTutorialId: string;
  setIsAiSettingsDropdownOpen: Dispatch<SetStateAction<boolean>>;
}) {
  const { track } = useTracker();
  const { t, data } = useLocalization();
  const settings = useRecoilValue(settingsState);
  const isLoadingEditor = useRecoilValue(editorLoadingState);
  const isLoggedIn = useRecoilValue(isLoggedInSelector);

  const mediasToDownload = useRecoilValue(downloadMediaState);

  const {
    aiToolsChoices,
    aiToolbarElements,
    isCtaActive,
    isCurrentPipelineDefaultForUser,
    isSavingSettings,
    lastAppliedAiToolsChoices,
    onApplyClick,
    onRemovePipelineStep,
    onSelectPreview,
    onToggleChange,
    onVariantsChange,
    shouldBlockPipelineStepForUser,
    storeNewDefaultAiPipeline,
  } = useAiToolbar({
    onCtaActiveChange,
    setIsAiSettingsDropdownOpen,
  });

  const tutorial = useRecoilValue<TutorialProps>(tutorialState);

  const saveSettingsRef = useRef(null);
  const gearButtonRef = useRef(null);
  useOnClickOutside(
    saveSettingsRef,
    [gearButtonRef],
    useCallback(() => {
      if (tutorial.isTutorialActive) {
        return;
      }
      setIsAiSettingsDropdownOpen(false);
    }, [setIsAiSettingsDropdownOpen, tutorial.isTutorialActive])
  );

  const getDropdownDivId = useCallback(
    (sectionKey: SectionName): string => {
      if (
        sectionKey === 'face' &&
        tutorial.id === newModelsTutorialId &&
        tutorial.currentStep === 1
      ) {
        return `${newModelsTutorialId}-step1`;
      }

      if (
        sectionKey === 'color' &&
        tutorial.id === newModelsTutorialId &&
        tutorial.currentStep === 2
      ) {
        return `${newModelsTutorialId}-step2`;
      }

      return '';
    },
    [newModelsTutorialId, tutorial.currentStep, tutorial.id]
  );

  return (
    <div
      className={`w-[290px] bg-gray-500 rounded-[16px] me-6 flex flex-col p-8 relative justify-between ${
        tutorial.id === saveSettingsTutorialId ? 'z-40' : ''
      }`}
    >
      {mediasToDownload.length > 1 && (
        <div className="bg-overlay-black-70 absolute w-[100%] h-[100%] z-20 top-0 left-0 flex items-center justify-center">
          <div className="backdrop-blur-[2px] max-w-[85%] bg-overlay-white-20 px-3 py-4 rounded-2xl flex flex-col items-center justify-center">
            <img className="block w-6 h-6" {...infoIcon} alt="warning" />
            <p className="text-[12px] font-medium text-center">
              {t(data.result.aiToolbarDisabledWithMultipleSelection)}
            </p>
          </div>
        </div>
      )}
      <div className="overflow-y-auto no-scrollbar">
        <div className="flex justify-between items-center">
          <p className="uppercase SB13 tracking-wider text-gray-300">
            {t(data.result.aiToolbar)}
          </p>
          {settings.values.shouldEnableSavingAiModelDefault && (
            <button
              type="button"
              ref={gearButtonRef}
              onClick={() => {
                if (tutorial.isTutorialActive) {
                  return;
                }

                track('ai_settings', 'saving_icon_tapped', {
                  isLoggedIn,
                  willOpen: !isAiSettingsDropdownOpen,
                });
                setIsAiSettingsDropdownOpen(!isAiSettingsDropdownOpen);
              }}
            >
              <div
                id={`${saveSettingsTutorialId}-step1`}
                className="flex justify-between items-center"
              >
                <img {...gear} alt="" />
                {isAiSettingsDropdownOpen ? (
                  <img {...upArrowIcon} className="ps-0.5" alt="" />
                ) : (
                  <img {...downArrowIcon} className="ps-0.5" alt="" />
                )}
              </div>
            </button>
          )}
        </div>

        {isAiSettingsDropdownOpen && (
          <div
            id={`${saveSettingsTutorialId}-step2`}
            ref={saveSettingsRef}
            className={`absolute start-60 top-16 flex justify-between items-start p-3 bg-gray-450 z-30 rounded-xl border border-overlay-white-10 w-52 ${
              isCurrentPipelineDefaultForUser || tutorial.isTutorialActive
                ? ''
                : 'hover:cursor-pointer hover:bg-gray-500'
            }`}
            onClick={async () => {
              if (tutorial.isTutorialActive) {
                return;
              }

              track('ai_settings', 'save_button_tapped', {
                aiPipeline: aiToolsChoices,
              });
              await storeNewDefaultAiPipeline();
            }}
          >
            {isSavingSettings ? (
              <div className="mt-0.5 ms-0.5 me-0.5">
                <Spinner size={10} color="white" />
              </div>
            ) : isCurrentPipelineDefaultForUser ? (
              <img {...greenCheckIcon} className="ps-0.5" alt="" />
            ) : (
              <img {...consent} className="ps-0.5" alt="" />
            )}
            <div className="ms-1.5">
              <p className="B12">
                {isCurrentPipelineDefaultForUser
                  ? t(data.result.settingsSavedAsDefaultTitle)
                  : t(data.result.saveSettingsAsDefaultTitle)}
              </p>
              <p className="M12 mt-1 text-gray-400">
                {isCurrentPipelineDefaultForUser
                  ? t(data.result.settingsSavedAsDefaultBody)
                  : t(data.result.saveSettingsAsDefaultBody)}
              </p>
            </div>
          </div>
        )}

        {aiToolbarElements.map((section: EditorSection) => {
          /* TODO support for legacy color correction, remove on cleanup */
          const shouldHideSectionTitle =
            section.pipelineSteps.length === 1 &&
            section.pipelineSteps[0].visualization ===
              ImageEditorElements.Toggle;

          return (
            <div
              key={section.key}
              className="my-0 py-6 border-b-1.5 border-overlay-white-10 last-of-type:border-transparent last-of-type:pb-0"
            >
              {!shouldHideSectionTitle && (
                <div
                  className={`flex mb-5 ${
                    tutorial.id === newModelsTutorialId &&
                    section.key === 'color' &&
                    tutorial.currentStep === 2
                      ? 'relative z-40'
                      : ''
                  }`}
                >
                  <img
                    className="w-9 h-9"
                    {...section.icon}
                    alt={`model-${section.key}`}
                  />
                  <div className="ms-3">
                    <p className="SB14 font-semibold">
                      {t(section.copy.title, {}, 'capitalize')}
                    </p>
                    <p className="M14 text-gray-400 mt-0.5">
                      {section.copy.subtitle}
                    </p>
                  </div>
                </div>
              )}

              {section.pipelineSteps.map((pipelineStep, index) => {
                const dropdownButtonDivId = getDropdownDivId(section.key);
                const shouldHighlightDropdown = dropdownButtonDivId !== '';

                return pipelineStep.visualization ===
                  ImageEditorElements.Toggle ? (
                  <Toggle
                    key={pipelineStep.tool}
                    id={pipelineStep.tool}
                    label={pipelineStep.title!}
                    iconPath={pipelineStep.iconPath}
                    name={pipelineStep.title!}
                    checked={pipelineStep.tool in aiToolsChoices}
                    additionalStyle={`w-[100%] ${
                      shouldHideSectionTitle ? 'mt-0' : 'mt-4'
                    }`}
                    tooltip={pipelineStep.tooltip}
                    onToggleChange={(event) =>
                      onToggleChange({
                        tool: pipelineStep.tool,
                        checked: event.checked,
                        model: pipelineStep.models[0].value,
                      })
                    }
                  />
                ) : pipelineStep.visualization ===
                  ImageEditorElements.Filters ? (
                  <EditorFilters
                    className={
                      index + 1 !== section.pipelineSteps.length ? 'mb-4' : ''
                    }
                    key={pipelineStep.tool}
                    onSelectedFilter={onVariantsChange}
                    selectedModel={aiToolsChoices[pipelineStep.tool]}
                    editorModels={pipelineStep.models}
                    tool={pipelineStep.tool}
                  />
                ) : (
                  <Dropdown
                    aiToolbarSection={section.key}
                    key={pipelineStep.tool}
                    id={pipelineStep.tool}
                    title={pipelineStep.title!}
                    iconPath={pipelineStep.iconPath!}
                    tooltip={pipelineStep.tooltip}
                    isOpen={false}
                    hasChangedDefaultChoice={
                      !equal(
                        lastAppliedAiToolsChoices[pipelineStep.tool],
                        aiToolsChoices[pipelineStep.tool]
                      )
                    }
                    dropdownButtonDivId={dropdownButtonDivId}
                    shouldHighlight={shouldHighlightDropdown}
                    isTutorialActive={tutorial.isTutorialActive}
                    isBusinessOnly={pipelineStep.isBlockedForPersonalUsers}
                  >
                    <EditorPreviews
                      aiToolbarSection={section.key}
                      pipelineStep={pipelineStep}
                      previews={pipelineStep.models.map((model) => ({
                        url: model.thumbnailPath,
                        label: model.title,
                        id: model.key,
                        value: model.value,
                      }))}
                      lastAppliedModel={
                        lastAppliedAiToolsChoices[pipelineStep.tool]
                      }
                      onRemovePipelineStep={onRemovePipelineStep}
                      onSelectPreview={onSelectPreview}
                      shouldBlockUser={shouldBlockPipelineStepForUser({
                        pipelineStep,
                      })}
                      isBusinessOnly={pipelineStep.isBlockedForPersonalUsers}
                    />
                  </Dropdown>
                );
              })}
            </div>
          );
        })}
      </div>

      <button
        id={`${newModelsTutorialId}-step3`}
        type="button"
        className={`btn btn--medium md:btn--large btn--primary mt-4 h-[42px] ${
          tutorial.id === newModelsTutorialId && tutorial.currentStep === 3
            ? 'z-40'
            : ''
        }`}
        disabled={
          (!isCtaActive || isLoadingEditor) &&
          !(tutorial.id === newModelsTutorialId && tutorial.currentStep === 3)
        }
        onClick={async () => {
          if (
            tutorial.id === newModelsTutorialId &&
            tutorial.currentStep === 3
          ) {
            return;
          }

          await onApplyClick();
        }}
      >
        <p className="M16">{t(data.common.apply)}</p>
      </button>
    </div>
  );
}
